"use client";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { useUserStore } from "@/_nucleo/store/_auth.store";
import { useRefreshAuthTokenQuery } from "@login";

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const { auth, setUserData } = useUserStore();
  const refreshLock = useRef(false); // evita múltiplos refreshes simultâneos

  // Só tenta refresh se temos dados de auth válidos
  const shouldTryRefresh = !!(
    auth?.token &&
    auth?.refreshToken &&
    auth?.expiresAt
  );

  const {
    data: refreshedAuth,
    isError: refreshError,
    isLoading: refreshLoading,
    refetch: refreshRefetch,
  } = useRefreshAuthTokenQuery(
    {
      token: auth?.token || "",
      refreshToken: auth?.refreshToken || "",
      expiresAt: auth?.expiresAt || new Date(),
    },
    shouldTryRefresh // enabled
  );

   // --- Função de refresh segura ---
  const tryRefreshToken = useCallback(async () => {
    if (!shouldTryRefresh || refreshLock.current) return;
    const now = Date.now();
    const expires = new Date(auth!.expiresAt).getTime();

    // ✅ Se já expirou, força logout imediatamente
    if (expires <= now) {
      localStorage.clear();
      sessionStorage.clear();
      router.push("/login");
      return;
    }

    // Só refaz se estiver perto de expirar (< 5min)
    if (expires - now <= 5 * 60 * 1000) {
      refreshLock.current = true;
      try {
        await refreshRefetch();
      } finally {
        refreshLock.current = false;
      }
    }
  }, [auth, refreshRefetch, router, shouldTryRefresh]);

  useEffect(() => {
    const forceLogout = () => {
      localStorage.clear();
      sessionStorage.clear();
      router.push("/login");
    };

    // --- 🔒 Logout diário (sem intervalos) ---
    const today = new Date().toDateString();
    const lastLoginDay = localStorage.getItem("lastLoginDay");

    if (!lastLoginDay) {
      localStorage.setItem("lastLoginDay", today);
    } else if (lastLoginDay !== today) {
      forceLogout();
    }

    // Verifica novamente quando o usuário volta pro app
    const handleFocus = () => {
      const currentDay = new Date().toDateString();
      if (localStorage.getItem("lastLoginDay") !== currentDay) {
        forceLogout();
      } else{
        tryRefreshToken();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [router, auth?.expiresAt, refreshRefetch, tryRefreshToken]);

    // --- Intervalo de refresh a cada 4 min (mais frequente) ---
  useEffect(() => {
    const interval = setInterval(() => {
      tryRefreshToken();
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [auth, shouldTryRefresh, tryRefreshToken]);
  
  useEffect(() => {
    // Se não tem sessão next-auth E não tem dados de auth na store
    if (status === "unauthenticated" && !shouldTryRefresh) {
      if (pathname !== "/login") router.push("/login");
      return;
    }

    // Se tentou refresh e falhou, redireciona
    if (shouldTryRefresh && refreshError && !refreshLoading) {
      if (pathname !== "/login") router.push("/login");
      return;
    }

    // Se refresh teve sucesso e retornou dados novos, atualiza store
    if (
      shouldTryRefresh &&
      refreshedAuth &&
      (refreshedAuth.token !== auth?.token ||
        refreshedAuth.refreshToken !== auth?.refreshToken)
    ) {
      setUserData({
        auth: {
          tenantId: auth?.tenantId || "",
          token: refreshedAuth.token,
          refreshToken: refreshedAuth.refreshToken,
          expiresAt: new Date(refreshedAuth.expiresAt),
        },
      });
      localStorage.setItem("lastLoginDay", new Date().toDateString());
    }
  }, [
    status,
    pathname,
    router,
    shouldTryRefresh,
    refreshError,
    refreshLoading,
    refreshedAuth,
    auth,
    setUserData,
  ]);

  return <>{children}</>;
}
